/**
 * Composable for gallery export: calls export API, handles 200 (blob) or 202 (job + poll), triggers download(s).
 */

import { ref } from 'vue'
import { apiClient } from '@/services/api'

export function useGalleryExport() {
  const isExporting = ref(false)
  const exportError = ref<string | null>(null)
  const exportJobStatus = ref<'idle' | 'processing' | 'completed' | 'failed'>('idle')
  const exportDownloadLinks = ref<Array<{ url: string; filename: string }>>([])

  const POLL_INTERVAL_MS = 2000
  const MAX_POLL_ATTEMPTS = 120 // 4 minutes

  function triggerBlobDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function exportGallery(galleryId: string): Promise<void> {
    isExporting.value = true
    exportError.value = null
    exportJobStatus.value = 'idle'
    exportDownloadLinks.value = []

    try {
      const result = await apiClient.exportGallery(galleryId, { metadataOnly: false })

      if (result.type === 'metadata') {
        exportError.value = 'Unexpected metadata-only response'
        return
      }

      if (result.type === 'blob') {
        triggerBlobDownload(result.data, result.filename)
        return
      }

      if (result.type === 'job') {
        exportJobStatus.value = 'processing'
        let attempts = 0
        const poll = async (): Promise<void> => {
          const status = await apiClient.getExportJobStatus(result.jobId)
          if (status.status === 'completed' && status.downloadLinks?.length) {
            exportJobStatus.value = 'completed'
            exportDownloadLinks.value = status.downloadLinks
            // Auto-download first part
            const first = status.downloadLinks[0]
            const blob = await apiClient.downloadExportPart(first.url)
            triggerBlobDownload(blob, first.filename)
            return
          }
          if (status.status === 'failed') {
            exportJobStatus.value = 'failed'
            exportError.value = status.error ?? 'Export failed'
            return
          }
          attempts++
          if (attempts >= MAX_POLL_ATTEMPTS) {
            exportJobStatus.value = 'failed'
            exportError.value = 'Export timed out'
            return
          }
          setTimeout(poll, POLL_INTERVAL_MS)
        }
        await poll()
        return
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      exportError.value = message
      exportJobStatus.value = 'failed'
    } finally {
      isExporting.value = false
    }
  }

  async function downloadExportPart(link: { url: string; filename: string }): Promise<void> {
    try {
      const blob = await apiClient.downloadExportPart(link.url)
      triggerBlobDownload(blob, link.filename)
    } catch (err: unknown) {
      exportError.value = err instanceof Error ? err.message : String(err)
    }
  }

  return {
    isExporting,
    exportError,
    exportJobStatus,
    exportDownloadLinks,
    exportGallery,
    downloadExportPart,
  }
}
